/**
 * Deterministic, offline APA-7 reference-list linter (issue #91).
 *
 * Pure string-in → findings-out. This module is intentionally SELF-CONTAINED: it does
 * NOT import or touch the citation-formatting core (`src/lib/citation.ts`,
 * `src/lib/bibliography.ts`) or any CSL fixture, so it can never regress a golden
 * snapshot. Every finding is *advisory* — a "likely issue to review", never a claim that
 * a reference is correct. The checks are deliberately high-precision (a false positive is
 * worse than a miss), matching the MVP rule set in the proposal.
 */

export type Confidence = "high" | "medium";

export interface Finding {
  /** Stable rule id (used as React key / for tests). */
  rule: string;
  /** Short human-readable label. */
  title: string;
  /** Explanation of what looks off and why. */
  message: string;
  /** Optional copy-to-clipboard suggested fix. */
  suggestion?: string;
  confidence: Confidence;
  /** "Why" link — an authoritative APA guideline or one of our own tools. */
  learnMoreUrl: string;
  learnMoreLabel: string;
}

export interface EntryReport {
  /** 1-based position in the pasted list. */
  position: number;
  /** The normalized single-line entry text that was checked. */
  text: string;
  findings: Finding[];
}

export interface CheckReport {
  entries: EntryReport[];
  /** List-level findings (ordering + duplicates) that span multiple entries. */
  listFindings: Finding[];
  totalEntries: number;
  totalFindings: number;
}

// --- Module-level regexes (Biome: regex must not live inside loops) ------------

const RE_BLANK_LINE = /\n[ \t]*\n/;
const RE_BLANK_LINE_SPLIT = /\n[ \t]*\n/;
const RE_NEWLINE = /\n/;
// APA 7 date element: (2020), (2020a), (n.d.), (n.d.-a), (in press), and the fuller
// "(2021, March 3)", "(2021, March)", "(2021, Spring)", "(2021, June 28–July 1)" forms used
// for webpages, news, blogs and conference papers. Group 1 is the year alone.
const RE_YEAR =
  /\((\d{4}[a-z]?|n\.d\.(?:-[a-z])?|in press)(?:,\s*(?:Spring|Summer|Fall|Autumn|Winter|(?:January|February|March|April|May|June|July|August|September|October|November|December)(?:\s+\d{1,2})?(?:\s*[-–—]\s*(?:(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+)?\d{1,2})?))?\)/;
// A personal-author initial ("Smith, J." or "J. M."), the mark of an inverted APA name.
const RE_INITIAL = /\b[A-Z]\.(?=\s|$|-|,)/;
// Words that make a no-comma author segment a group (organisation) author, which APA
// writes out in full, never inverted and never with "&" between name parts.
const RE_GROUP_AUTHOR_WORD =
  /\b(?:Association|Society|University|College|School|Institute|Institution|Department|Ministry|Organization|Organisation|Company|Corporation|Council|Committee|Commission|Foundation|Center|Centre|Centers|Centres|Agency|Bureau|Board|Authority|Administration|Service|Services|Group|Office|Academy|Museum|Library|Hospital|Federation|Alliance|Union|Fund|Trust|Consortium|Network|Laboratory|Laboratories|Government|Parliament|Congress|Bank|Initiative|Programme|Program|Division|Inc|Ltd|LLC|Nations|Team)\b/;
const RE_LEADING_ARTICLE = /^(?:a|an|the)\s+/i;
const RE_BAD_DOI = /(doi:\s*10\.|dx\.doi\.org|http:\/\/(?:dx\.)?doi\.org)/i;
const RE_BARE_DOI_SCHEME = /\bdoi:\s*(10\.\S+)/i;
const RE_DX_OR_HTTP_DOI = /https?:\/\/(?:dx\.)?doi\.org\/(\S+)/i;
const RE_ENDS_WITH_URL = /(https?:\/\/\S+|\b10\.\d{4,}\/\S+)$/;
const RE_TERMINAL_PUNCT = /[.?!]$/;
const RE_AND_CONNECTOR = /,?\s+and\s+(?=[A-Z])/;
const RE_FIRST_LAST = /^[A-Z][a-z]+(?:\s+[A-Z]\.?)*\s+[A-Z][a-z]+\b/;
const RE_TWO_WORD_NAME = /^([A-Z][a-z]+)\s+([A-Z][a-z]+)$/;
const RE_TITLE_AFTER_YEAR = /^\.?\s*([^.]+?)\.(?:\s|$)/;
const RE_NON_LETTER = /[^a-z]/g;
const RE_NON_LETTER_KEEP_CASE = /[^a-zA-Z]/g;
const RE_WORD_SPLIT = /\s+/;
const RE_PUNCT_STRIP = /[^a-z0-9]+/g;
const RE_LEADING_CAP = /^[A-Z]/;
const RE_TRAILING_COMMA = /,$/;
const RE_PIPES = /\|/g;

// APA guideline references reused across findings.
const APA_ELEMENTS_URL =
  "https://apastyle.apa.org/style-grammar-guidelines/references/elements-list-entry";
const APA_EXAMPLES_URL =
  "https://apastyle.apa.org/style-grammar-guidelines/references/examples";
const APA_DOI_URL =
  "https://apastyle.apa.org/style-grammar-guidelines/references/dois-urls";

/**
 * Split pasted text into individual reference entries. Blank-line-separated blocks are
 * treated as one entry each (wrapped/hanging-indent lines are rejoined with a space);
 * otherwise each non-empty line is its own entry.
 */
export function splitEntries(text: string): string[] {
  const trimmed = text.trim();
  if (!trimmed) {
    return [];
  }
  if (RE_BLANK_LINE.test(trimmed)) {
    return trimmed
      .split(RE_BLANK_LINE_SPLIT)
      .map((block) =>
        block
          .split(RE_NEWLINE)
          .map((line) => line.trim())
          .join(" ")
          .trim()
      )
      .filter(Boolean);
  }
  return trimmed
    .split(RE_NEWLINE)
    .map((line) => line.trim())
    .filter(Boolean);
}

/** The author segment is everything before the first "(year)". */
function authorSegment(entry: string): string | null {
  const match = RE_YEAR.exec(entry);
  if (!match || match.index === 0) {
    return null;
  }
  return entry.slice(0, match.index).trim().replace(RE_TRAILING_COMMA, "");
}

/** Rule 4 — a missing `(YYYY).` / `(n.d.)` year element near the start of the entry. */
function checkYear(entry: string): Finding | null {
  if (RE_YEAR.test(entry)) {
    return null;
  }
  return {
    rule: "missing-year",
    title: "No publication year detected",
    message:
      "APA reference entries place the year in parentheses right after the author — e.g. (2020). or (n.d.) when there is no date. No (YYYY) element was found in this entry.",
    confidence: "high",
    learnMoreUrl: APA_ELEMENTS_URL,
    learnMoreLabel: "APA: reference elements",
  };
}

/** Build "Smith, J." from a simple "First Last" author segment, when unambiguous. */
function invertSimpleName(authorSeg: string): string | null {
  const match = RE_TWO_WORD_NAME.exec(authorSeg);
  if (!match) {
    return null;
  }
  const [, first, last] = match;
  return `${last}, ${first.charAt(0)}.`;
}

/** Rule 3a — author looks like "First Last" instead of APA "Last, F. M.". */
function checkAuthorOrder(authorSeg: string): Finding | null {
  if (authorSeg.includes(",") || RE_GROUP_AUTHOR_WORD.test(authorSeg)) {
    return null;
  }
  if (!RE_FIRST_LAST.test(authorSeg)) {
    return null;
  }
  const suggestion = invertSimpleName(authorSeg);
  return {
    rule: "author-name-order",
    title: 'Author may be in "First Last" order',
    message:
      'APA inverts every author name to "Surname, Initials" (e.g. "Smith, J. M."). This entry appears to lead with a given name. Confirm the author is inverted.',
    suggestion: suggestion ?? undefined,
    confidence: suggestion ? "high" : "medium",
    learnMoreUrl: APA_ELEMENTS_URL,
    learnMoreLabel: "APA: author element",
  };
}

/** Rule 3b — "and" instead of "&" before the final author. */
function checkAndConnector(authorSeg: string): Finding | null {
  if (authorSeg.includes("&") || !RE_AND_CONNECTOR.test(authorSeg)) {
    return null;
  }
  // "Procter and Gamble", "Johnson and Johnson": a group author's own "and" stays. Only a
  // list of personal authors (inverted names with initials) takes "&" before the last.
  if (!RE_INITIAL.test(authorSeg) || RE_GROUP_AUTHOR_WORD.test(authorSeg)) {
    return null;
  }
  return {
    rule: "and-vs-ampersand",
    title: 'Use "&" before the final author',
    message:
      'In an APA reference list, separate the last two authors with an ampersand ("&"), not the word "and". ("and" is only used in narrative in-text citations.)',
    suggestion: authorSeg.replace(RE_AND_CONNECTOR, ", & "),
    confidence: "high",
    learnMoreUrl: APA_ELEMENTS_URL,
    learnMoreLabel: "APA: author element",
  };
}

/** Rule 5 — bare `doi:` / `dx.doi.org` / `http://` DOI instead of the APA https form. */
function checkDoi(entry: string): Finding | null {
  if (!RE_BAD_DOI.test(entry)) {
    return null;
  }
  const scheme = RE_BARE_DOI_SCHEME.exec(entry);
  const dx = RE_DX_OR_HTTP_DOI.exec(entry);
  const doiTail = scheme?.[1] ?? dx?.[1];
  return {
    rule: "doi-url-format",
    title: "DOI is not in the APA URL format",
    message:
      "APA 7 formats every DOI as a full https link: https://doi.org/10.xxxx/yyyy — not a bare doi: prefix, a dx.doi.org host, or an http:// link.",
    suggestion: doiTail ? `https://doi.org/${doiTail}` : undefined,
    confidence: "high",
    learnMoreUrl: APA_DOI_URL,
    learnMoreLabel: "APA: DOIs & URLs",
  };
}

/** Rule 6 — entry does not end in a period (and does not end in a URL/DOI). */
function checkTerminalPeriod(entry: string): Finding | null {
  const trimmed = entry.trim();
  if (RE_ENDS_WITH_URL.test(trimmed) || RE_TERMINAL_PUNCT.test(trimmed)) {
    return null;
  }
  return {
    rule: "terminal-period",
    title: "Entry does not end with a period",
    message:
      "Most APA reference entries end with a period. (The exception is an entry ending in a DOI or URL, which takes no closing period.) This entry ends without terminal punctuation.",
    confidence: "high",
    learnMoreUrl: APA_ELEMENTS_URL,
    learnMoreLabel: "APA: reference elements",
  };
}

/** Significant words = 4+ letters, used for the Title Case heuristic. */
function significantWords(title: string): string[] {
  return title
    .trim()
    .split(RE_WORD_SPLIT)
    .slice(1) // sentence case always capitalizes the first word
    .filter((w) => w.replace(RE_NON_LETTER_KEEP_CASE, "").length >= 4);
}

/**
 * Rule 7 — a source/article title typed in Title Case where APA wants sentence case.
 * High-precision: only fires when EVERY significant (4+ letter) word after the first is
 * capitalized — a real sentence-case title with a couple of proper nouns keeps lowercase
 * content words and so will not trip this.
 */
function checkTitleCase(entry: string): Finding | null {
  const yearMatch = RE_YEAR.exec(entry);
  if (!yearMatch) {
    return null;
  }
  const afterYear = entry.slice(yearMatch.index + yearMatch[0].length);
  const titleMatch = RE_TITLE_AFTER_YEAR.exec(afterYear);
  if (!titleMatch) {
    return null;
  }
  const words = significantWords(titleMatch[1]);
  if (words.length < 3) {
    return null;
  }
  const allCapitalized = words.every((w) => RE_LEADING_CAP.test(w));
  if (!allCapitalized) {
    return null;
  }
  return {
    rule: "title-case-smell",
    title: "Title may be in Title Case",
    message:
      'APA uses sentence case for article, chapter, and book titles — capitalize only the first word, the first word after a colon, and proper nouns (e.g. "The effects of climate change on coral reefs"). This title looks like it is in Title Case.',
    confidence: "medium",
    learnMoreUrl: APA_EXAMPLES_URL,
    learnMoreLabel: "APA: title case vs. sentence case",
  };
}

/** Run all per-entry rules for a single normalized entry. */
function checkEntry(entry: string): Finding[] {
  const findings: Finding[] = [];
  const yearFinding = checkYear(entry);
  if (yearFinding) {
    findings.push(yearFinding);
  }
  const authorSeg = authorSegment(entry);
  if (authorSeg) {
    const order = checkAuthorOrder(authorSeg);
    if (order) {
      findings.push(order);
    }
    const connector = checkAndConnector(authorSeg);
    if (connector) {
      findings.push(connector);
    }
  }
  const doi = checkDoi(entry);
  if (doi) {
    findings.push(doi);
  }
  const period = checkTerminalPeriod(entry);
  if (period) {
    findings.push(period);
  }
  const titleCase = checkTitleCase(entry);
  if (titleCase) {
    findings.push(titleCase);
  }
  return findings;
}

/** First-author surname (or article-stripped title) used for ordering + duplicate keys. */
function sortKey(entry: string): string {
  const authorSeg = authorSegment(entry);
  let basis: string;
  if (authorSeg) {
    basis = authorSeg.includes(",")
      ? authorSeg.slice(0, authorSeg.indexOf(","))
      : (authorSeg.split(RE_WORD_SPLIT).pop() ?? authorSeg);
  } else {
    basis = entry.replace(RE_LEADING_ARTICLE, "");
  }
  return basis.toLowerCase().replace(RE_NON_LETTER, "");
}

/** Rule 1 — flag entries that break alphabetical (A–Z by surname) order. */
function checkOrdering(entries: string[]): Finding[] {
  const findings: Finding[] = [];
  const keys = entries.map(sortKey);
  for (let i = 1; i < keys.length; i++) {
    const prev = keys[i - 1];
    const cur = keys[i];
    if (prev && cur && prev.localeCompare(cur) > 0) {
      findings.push({
        rule: "list-ordering",
        title: `Entry ${i + 1} may be out of alphabetical order`,
        message: `APA reference lists are ordered A–Z by the first author's surname. Entry ${i + 1} sorts before entry ${i} ("${cur}" < "${prev}"). Check the ordering of these two entries.`,
        confidence: "high",
        learnMoreUrl: APA_ELEMENTS_URL,
        learnMoreLabel: "APA: reference list order",
      });
    }
  }
  return findings;
}

/** Near-duplicate identity: surname + year + first significant title word. */
function duplicateKey(entry: string): string {
  const surname = sortKey(entry);
  const yearMatch = RE_YEAR.exec(entry);
  const year = yearMatch ? yearMatch[1] : "";
  const afterYear = yearMatch
    ? entry.slice(yearMatch.index + yearMatch[0].length)
    : entry;
  const titleMatch = RE_TITLE_AFTER_YEAR.exec(afterYear);
  const titleWord = (titleMatch?.[1] ?? "")
    .trim()
    .split(RE_WORD_SPLIT)
    .slice(0, 4)
    .join("")
    .toLowerCase()
    .replace(RE_PUNCT_STRIP, "");
  return `${surname}|${year}|${titleWord}`;
}

/** Rule 2 — flag near-duplicate entries (same surname + year + title start). */
function checkDuplicates(entries: string[]): Finding[] {
  const findings: Finding[] = [];
  const seen = new Map<string, number>();
  for (let i = 0; i < entries.length; i++) {
    const key = duplicateKey(entries[i]);
    if (!key || key.replace(RE_PIPES, "") === "") {
      continue;
    }
    const firstIndex = seen.get(key);
    if (firstIndex === undefined) {
      seen.set(key, i);
      continue;
    }
    findings.push({
      rule: "duplicate-entry",
      title: `Entry ${i + 1} may duplicate entry ${firstIndex + 1}`,
      message: `Entries ${firstIndex + 1} and ${i + 1} share the same author, year, and title start. A reference list should list each source once.`,
      confidence: "high",
      learnMoreUrl: APA_ELEMENTS_URL,
      learnMoreLabel: "APA: reference list",
    });
  }
  return findings;
}

/**
 * Run the full deterministic APA-7 reference-list check over pasted text. Pure — no
 * network, no mutation, no dependency on the citation core.
 */
export function checkReferenceList(text: string): CheckReport {
  const rawEntries = splitEntries(text);
  const entries: EntryReport[] = rawEntries.map((entry, index) => ({
    position: index + 1,
    text: entry,
    findings: checkEntry(entry),
  }));
  const listFindings = [
    ...checkOrdering(rawEntries),
    ...checkDuplicates(rawEntries),
  ];
  const totalFindings =
    listFindings.length +
    entries.reduce((sum, e) => sum + e.findings.length, 0);
  return {
    entries,
    listFindings,
    totalEntries: entries.length,
    totalFindings,
  };
}
